import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Check, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import Stars from "@/components/site/Stars";
import { api, errDetail } from "@/lib/api";
import { ConfirmDelete } from "@/pages/admin/shared";
import type { Review } from "@/types";

const FILTERS = [["pending", "Pendientes"], ["approved", "Publicadas"], ["all", "Todas"]] as const;

export default function AdminReviews() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number][0]>("pending");
  const params = filter === "all" ? {} : { approved: filter === "approved" };
  const { data = [], refetch } = useQuery<Review[]>({ queryKey: ["admin-reviews", filter], queryFn: async () => (await api.get("/admin/reviews", { params })).data });
  const patch = async (id: string, body: Partial<Pick<Review, "approved" | "featured">>, msg: string) => {
    try { await api.patch(`/admin/reviews/${id}`, body); toast.success(msg); refetch(); } catch (e) { toast.error(errDetail(e)); }
  };
  const remove = async (id: string) => { await api.delete(`/admin/reviews/${id}`); toast.success("Reseña eliminada"); refetch(); };
  return (
    <div className="max-w-5xl" data-testid="admin-reviews-page">
      <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Reseñas</h1>
      <div className="mt-6 flex gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-reviews-filter-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${filter === k ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`}>{label}</button>
        ))}
      </div>
      <div className="mt-8 space-y-3">
        {data.length === 0 && <p className="text-sm text-slate-500" data-testid="admin-reviews-empty">No hay reseñas en esta vista.</p>}
        {data.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white rounded-lg border border-slate-200 p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Stars value={r.rating} testId={`admin-review-stars-${r.id}`} />
                  <span className="font-semibold text-[#0B192C]">{r.name}</span>
                  {r.country && <span className="text-xs text-slate-500">· {r.country}</span>}
                  {r.yacht_name && <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{r.yacht_name}</span>}
                  {r.verified && <BadgeCheck className="h-4 w-4 text-[#008C86]" />}
                  {r.featured && <Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />}
                  <span className={`text-[11px] rounded-full px-2 py-0.5 ${r.approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.approved ? "Publicada" : "Pendiente"}</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{r.comment}</p>
                <p className="mt-2 text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("es-MX")} · {r.lang.toUpperCase()}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.approved
                  ? <button onClick={() => patch(r.id, { approved: false }, "Reseña ocultada")} data-testid={`admin-review-hide-${r.id}`} className="p-2 text-slate-500 hover:text-amber-600" aria-label="Ocultar"><X className="h-4 w-4" /></button>
                  : <button onClick={() => patch(r.id, { approved: true }, "Reseña publicada")} data-testid={`admin-review-approve-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><Check className="h-3.5 w-3.5" />Aprobar</button>}
                <button onClick={() => patch(r.id, { featured: !r.featured }, r.featured ? "Ya no es destacada" : "Marcada como destacada")} data-testid={`admin-review-feature-${r.id}`} className={`p-2 ${r.featured ? "text-[#D4AF37]" : "text-slate-400 hover:text-[#D4AF37]"}`} aria-label="Destacar"><Star className={`h-4 w-4 ${r.featured ? "fill-[#D4AF37]" : ""}`} /></button>
                <ConfirmDelete title={`¿Eliminar la reseña de ${r.name}?`} onConfirm={() => remove(r.id)}>
                  <button data-testid={`admin-review-delete-${r.id}`} className="p-2 text-red-500 hover:text-red-700" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
                </ConfirmDelete>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
